import React, { useContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import _ from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { useOpenChipNotification } from '../../../utils/openChipNotification';
import Comment from '../../../components/Comments/Comment';
import { TODO_TYPE } from '../../../constants/comments';
import { alterComments, reopenComment, resolveComment } from '../../../api/comments';
import { addCommentToMarket, addMarketComments, filterToRoot } from '../../../contexts/CommentsContext/commentsContextHelper';
import { CommentsContext } from '../../../contexts/CommentsContext/CommentsContext';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import { formInvestibleAddCommentLink, formMarketAddInvestibleLink, navigate } from '../../../utils/marketIdPathFunctions';
import { findMessagesForCommentIds, removeMessagesForCommentId } from '../../../utils/messageUtils';
import { NotificationsContext } from '../../../contexts/NotificationsContext/NotificationsContext';
import { stripHTML } from '../../../utils/stringFunctions';
import BugListItem from '../../../components/Comments/BugListItem';
import getReducer, { contractAll, expandAll, expandOrContract } from '../../../components/Comments/BugListContext';
import { getDeterminateReducer } from '../../../contexts/ContextUtils';
import { GmailTabItem, GmailTabs } from '../../../containers/Tab/Inbox';
import { BugReport, Eject, ExpandLess } from '@material-ui/icons';
import TooltipIconButton from '../../../components/Buttons/TooltipIconButton';
import { useButtonColors } from '../../../components/Buttons/ButtonConstants';
import { getNewBugNotifications } from '../../../components/Comments/Options';
import { todoClasses } from '../../Dialog/Planning/MarketTodos';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { sortInProgress } from '../../../containers/CommentBox/CommentBox';
import { useHotkeys } from 'react-hotkeys-hook';
import { BLUE_LEVEL, YELLOW_LEVEL } from '../../../constants/notifications';
import AddIcon from '@material-ui/icons/Add';
import { JOB_COMMENT_WIZARD_TYPE } from '../../../constants/markets';
import { ThemeModeContext } from '../../../contexts/ThemeModeContext';
import { useSearchMatchNavigation } from '../../../utils/searchMatchNavigation';

export function orderCondensedTasks(comments, investibleComments) {
  return sortInProgress(_.orderBy(comments, ['updated_at', 'body'], ['desc', 'asc']), investibleComments);
}

function CondensedTodos(props) {
  const {
    comments = [],
    investibleComments,
    marketInfo,
    marketId,
    groupId,
    isInbox = false,
    hideTabs = false,
    usePadding = true,
    isDefaultOpen = false,
    defaultToOpenComments = true,
    removeActions = true,
    useColor = true,
    expandTasksNotSection=false,
    sectionTitle,
    hidden,
    hash,
    maxWidth,
    isSearch = false,
    showCommentAdd = false,
    hideTitle = false,
    showChecked = true,
    inNotesTab = false
  } = props
  const classes = todoClasses();
  const openChipNotification = useOpenChipNotification();
  const { firstCommentMatch, openMatch } = useSearchMatchNavigation();
  const intl = useIntl();
  const history = useHistory();
  const [themeMode] = useContext(ThemeModeContext);
  const isDark = themeMode === 'dark';
  const { warningColor } = useButtonColors();
  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down('sm'));
  const { actionButtonColor } = useButtonColors();
  const [commentState, commentDispatch] = useContext(CommentsContext);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const [messagesState] = useContext(NotificationsContext);
  const [showOpen, setShowOpen] = useState(defaultToOpenComments);
  const [sectionOpen, setSectionOpen] = useState(expandTasksNotSection || isDefaultOpen);
  const [tasksOpen, setTasksOpen] = useState(isDefaultOpen);
  const [todoState, todoDispatch] = useReducer(getReducer(), {expansionState: {}});
  const [determinateState, determinateDispatch] = useReducer(getDeterminateReducer(),
    {determinate: {}, indeterminate: false, checkAll: false});
  const { indeterminate, determinate, checkAll } = determinateState;
  const { expansionState } = todoState;
  const openComments = comments.filter((comment) => !comment.resolved);
  const resolvedComments = comments.filter((comment) => comment.resolved);
  const tabCommentsRaw = inNotesTab ? comments : (showOpen ? openComments : resolvedComments);
  const tabComments = orderCondensedTasks(tabCommentsRaw, investibleComments);
  const openMatchTarget = isSearch && firstCommentMatch(orderCondensedTasks(openComments, investibleComments));
  const resolvedMatchTarget = isSearch && firstCommentMatch(orderCondensedTasks(resolvedComments, investibleComments));
  const resolvedCommentIds = resolvedComments.map((comment) => comment.id);
  const resolvedCommentsReplies = (investibleComments || []).filter((comment) =>
    resolvedCommentIds.includes(comment.root_comment_id) && comment.root_comment_id !== comment.id);
  const resolvedTodoMessages = findMessagesForCommentIds(
    resolvedCommentIds.concat(resolvedCommentsReplies.map((comment) => comment.id)), messagesState.messages, true);
  const hasResolvedTodoMessages = !_.isEmpty(resolvedTodoMessages);

  useEffect(() => {
    if (hash && hash.length > 1 && !hidden && !hash.includes('header')) {
      const found = investibleComments.find((comment) => hash.includes(comment.id));
      if (!_.isEmpty(found)) {
        const rootComment = filterToRoot(investibleComments, found.id);
        if (rootComment?.investible_id && rootComment.comment_type === TODO_TYPE && rootComment.resolved) {
          setSectionOpen(true);
          if (showOpen) {
            setShowOpen(false);
          }
          if (!expansionState[rootComment.id]) {
            todoDispatch(expandOrContract(rootComment.id));
          }
        }
      }
    }
  }, [hash, hidden, investibleComments, showOpen, expansionState]);

  function getRows() {
    if (!sectionOpen) {
      return <div style={{marginBottom: '1rem'}} key={`${showOpen}empty`}/>
    }
    return tabComments.map((comment) => {
      const { id, body, updated_at: updatedAt, resolved } = comment;
      const replies = comments.filter(aComment => aComment.root_comment_id === id && aComment.id !== id) || [];
      const expansionPanel = <div id={resolved ? `c${id}` : `condensed${id}`}
                                  style={{marginBottom: '1rem', marginRight: '1rem', marginLeft: '1rem'}}>
        <Comment
          marketId={marketId}
          comment={comment}
          comments={investibleComments}
          investibleComments={investibleComments}
          allowedTypes={[TODO_TYPE]}
          marketInfo={marketInfo}
          noAuthor
          isInbox={isInbox}
          removeActions={isInbox && removeActions}
          stagePreventsActions={isInbox && removeActions}
          idPrepend={resolved ? 'c' : 'condensed'}
          inNotesTab={inNotesTab}
          // B-all-641: open tasks in the Tasks tab already show associated notes
          // when expanded. Resolved tasks only appear in this overview list, so
          // the same expansion must show those notes. Skip it in the Notes tab,
          // which already renders them beside the task.
          showNotes={!inNotesTab}
        />
      </div>
      const determinateChecked = determinate[id];
      const checked = determinateChecked !== undefined ? determinateChecked : checkAll;
      return (
        <BugListItem id={id} replyNum={replies.length + 1} title={stripHTML(body)} useMinWidth={false}
                     newMessages={isInbox ? undefined : getNewBugNotifications(comment, messagesState)}
                     date={intl.formatDate(updatedAt)} showChecked={showChecked}
                     useSelect={!isInbox && showOpen} expansionPanel={expansionPanel} checked={checked}
                     expansionOpen={!!expansionState[id]} 
                     determinateDispatch={determinateDispatch}
                     bugListDispatch={todoDispatch} notificationType="todo" />
      );
    });
  }

  function onDrop(event, isOpen) {
    const commentId = event.dataTransfer.getData('text');
    setOperationRunning(true);
    removeMessagesForCommentId(commentId, messagesState);
    const target = event.target;
    target.style.cursor = 'wait';
    return isOpen ? resolveComment(marketId, commentId) : reopenComment(marketId, commentId)
      .then((comment) => {
        addCommentToMarket(comment, commentState, commentDispatch);
        setOperationRunning(false);
        return comment;
      }).finally(() => {
      target.style.cursor = 'pointer';
      setOperationRunning(false);
    });
  }

  function onDropOpen(event) {
    onDrop(event, true);
  }

  function onDropResolved(event) {
    onDrop(event, false);
  }

  function moveToBugs(bugType) {
    const selected = Object.keys(determinate).filter((key) => determinate[key]);
    const checked = checkAll ? tabComments.filter((comment) => determinate[comment.id] !== false)
      .map((comment) => comment.id) : selected;
    setOperationRunning(true);
    if (!_.isEmpty(checked)) {
      return alterComments(marketId, checked, bugType).then((comments) => {
        determinateDispatch({type: 'clear'});
        addMarketComments(commentDispatch, marketId, comments);
        setOperationRunning(false);
      });
    }
  }

  function moveSelected() {
    const selected = Object.keys(determinate).filter((key) => determinate[key]);
    const checked = checkAll ? tabComments.filter((comment) => determinate[comment.id] !== false)
        .map((comment) => comment.id) : selected;
    let checkedString;
    if (!_.isEmpty(checked)) {
      checked.forEach((anId) => {
        if (checkedString) {
          checkedString += `&fromCommentId=${anId}`;
        } else {
          checkedString = `&fromCommentId=${anId}`;
        }
      });
      determinateDispatch({type: 'clear'});
      if (checkedString) {
        navigate(history, `${formMarketAddInvestibleLink(marketId, groupId)}${checkedString}`);
      }
    }
  }

  function toggleTodos() {
    if (expandTasksNotSection) {
      if (tasksOpen) {
        todoDispatch(contractAll(tabComments));
      } else {
        todoDispatch(expandAll(tabComments));
      }
      setTasksOpen(!tasksOpen);
    } else {
      setSectionOpen(!sectionOpen);
    }
  }

  const myToggleTodosOpen = expandTasksNotSection ? tasksOpen : sectionOpen;
  useHotkeys('ctrl+alt+e', ()=> {
    if (expandTasksNotSection) {
      todoDispatch(expandAll(tabComments));
      setTasksOpen(true);
    } else {
      setSectionOpen(true);
    }
  }, {enableOnContentEditable: true}, []);
  useHotkeys('ctrl+shift+e', ()=> {
    if (expandTasksNotSection) {
      todoDispatch(contractAll(tabComments));
      setTasksOpen(false);
    } else {
      setSectionOpen(false);
    }
  }, {enableOnContentEditable: true}, []);

  const indicatorColor = isDark ? 'white' : 'black';

  return (
    <div className={sectionOpen ? classes.outerBorder : undefined} id="investibleCondensedTodos"
         style={{marginLeft: usePadding ? '1rem' : undefined, maxWidth}}>
      {!hideTitle && (
        <div style={{display: 'flex', alignItems: 'center', marginTop: isInbox ? '1rem' : undefined}}>
        <h2 id="tasksOverview" style={{paddingBottom: 0, marginBottom: 0, marginTop: 0, paddingTop: 0}}>
          <FormattedMessage id={!_.isEmpty(sectionTitle) ? sectionTitle : 'taskSection'} />{showCommentAdd
                     && <TooltipIconButton id="newTask"
                        marginLeft='1rem'
                        onClick={() => navigate(history,
                          formInvestibleAddCommentLink(JOB_COMMENT_WIZARD_TYPE, marketInfo.investible_id, marketId, TODO_TYPE))}
                        icon={<AddIcon fontSize='small' />}
                        translationId={`createNew${TODO_TYPE}${mobileLayout ? 'Mobile' : ''}`}
                      />}
        </h2>
        <IconButton onClick={toggleTodos} style={{marginBottom: 0, paddingBottom: 0, paddingTop: '5px'}}>
          <Tooltip key='toggleTodos'
                   title={<FormattedMessage id={`${myToggleTodosOpen ? 'closeTodos' : 'openTodos'}Tip`} />}>
            {myToggleTodosOpen ?
              <ExpandLess fontSize='small' /> :
            <ExpandMoreIcon fontSize='small' />}
          </Tooltip>
        </IconButton>
      </div>
      )}
      {!hideTabs && sectionOpen && (
        <GmailTabs
          removeBoxShadow
          useColor={useColor}
          value={showOpen ? 0 : 1}
          onChange={(event, value) => {
            setShowOpen(value === 0);
          }}
          indicatorColors={[indicatorColor, indicatorColor]}
          style={{ paddingBottom: '1rem' }}>
          <GmailTabItem label={intl.formatMessage({id: 'openHeader'})}
                        onTagClick={openMatchTarget ? () => openMatch(openMatchTarget) : undefined}
                        tagTooltipId={isSearch ? 'searchGoToMatch' : undefined}
                        color='black' tagLabel={isSearch ? intl.formatMessage({ id: 'match' }) : intl.formatMessage({id: 'total'})}
                        tag={_.size(openComments) > 0 ? `${_.size(openComments)}` : undefined}
                        onDrop={onDropOpen} toolTipId='openTasksToolTip'
                        onDragOver={(event)=>event.preventDefault()}/>
          {/* B-all-480: resolved count only grows so it does not display - only new message and
             search match counts */}
          <GmailTabItem label={intl.formatMessage({id: 'closedComments'})}
                        onTagClick={isSearch ? (resolvedMatchTarget ? () => openMatch(resolvedMatchTarget) : undefined)
                          : (hasResolvedTodoMessages ? () => openChipNotification(resolvedTodoMessages) : undefined)}
                        tagTooltipId={isSearch ? 'searchGoToMatch' : undefined}
                        tagColor={hasResolvedTodoMessages ? warningColor : undefined}
                        color='black'
                        tagLabel={isSearch ? intl.formatMessage({ id: 'match' }) : intl.formatMessage({id: 'new'})}
                        tag={isSearch ? (_.size(resolvedComments) > 0 ? `${_.size(resolvedComments)}` : undefined) : (hasResolvedTodoMessages ?
                          `${_.size(resolvedTodoMessages)}` : undefined)}
                        onDrop={onDropResolved} toolTipId='resolvedTasksToolTip'
                        onDragOver={(event)=>event.preventDefault()} />
        </GmailTabs>
      )}
      {!_.isEmpty(tabComments) && showChecked && sectionOpen && (
        <div style={{paddingBottom: '0.25rem'}}>
          <div style={{display: 'flex', width: '80%'}}>
            {showOpen && !mobileLayout && !isInbox && (
              <Checkbox style={{padding: 0, marginLeft: '0.6rem'}}
                        checked={checkAll}
                        indeterminate={indeterminate}
                        onChange={() => determinateDispatch({type: 'toggle'})}
              />
            )}
            {showOpen && (checkAll || !_.isEmpty(determinate)) && (
              <TooltipIconButton
                icon={<Eject htmlColor={actionButtonColor} />}
                onClick={moveSelected} translationId="todosMove" />
            )}
            {showOpen && (checkAll || !_.isEmpty(determinate)) && !isInbox && (
              <TooltipIconButton
                icon={<BugReport htmlColor='#e6e969' />}
                onClick={() => moveToBugs(YELLOW_LEVEL)} translationId="moveTodoYellow" />
            )}
            {showOpen && (checkAll || !_.isEmpty(determinate)) && !isInbox && (
              <TooltipIconButton
                icon={<BugReport htmlColor='#2F80ED' />}
                onClick={() => moveToBugs(BLUE_LEVEL)} translationId="moveTodoBlue" />
            )}
            <TooltipIconButton icon={<ExpandLess style={{marginLeft: '0.25rem'}} htmlColor={actionButtonColor} />}
                               onClick={() => todoDispatch(contractAll(tabComments))}
                               translationId="inboxCollapseAll" />
            <TooltipIconButton icon={<ExpandMoreIcon style={{marginLeft: '0.25rem'}} htmlColor={actionButtonColor} />}
                               onClick={() => todoDispatch(expandAll(tabComments))}
                               translationId="inboxExpandAll" />
          </div>
        </div>
      )}
      {_.isEmpty(tabComments) && showOpen && sectionOpen && !inNotesTab && (
        <Typography style={{marginTop: '2rem', maxWidth: '40rem', marginLeft: 'auto', marginRight: 'auto'}}
                    variant="body1">
          No open tasks.
        </Typography>
      )}
      {_.isEmpty(tabComments) && !showOpen && sectionOpen && (
        <Typography style={{marginTop: '2rem', maxWidth: '40rem', marginLeft: 'auto', marginRight: 'auto'}}
                    variant="body1">
          No resolved tasks.
        </Typography>
      )}
      {getRows()}
    </div>
  )
}

CondensedTodos.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.object),
  marketId: PropTypes.string.isRequired,
  hideTabs: PropTypes.bool
};

export default CondensedTodos;
